// crewbit-admin/src/components/money/RequiresActiveBusinessGate.tsx
//
// Layer 1 enforcement of the active-business gate per WIRING §2.5.1.
// Wraps Money routes (FinancialsPage, future /invoices, /expenses pages).
// When the user belongs to multiple businesses and hasn't picked one,
// renders the empty-state picker prompt instead of the screen body.
//
// Single-membership and zero-membership users pass through.
//
// Layer 2 (mutation hook short-circuits) ships in B.3 hook by hook.

import type { ReactNode } from 'react';
import { Building2 } from 'lucide-react';
import { palette, admin } from '../../styles/tokens';
import { useActiveBusiness, useActiveBusinessStore } from '../../hooks/useActiveBusiness';

interface Props {
  children: ReactNode;
}

export default function RequiresActiveBusinessGate({ children }: Props) {
  const { requiresSelection, memberships } = useActiveBusiness();
  if (requiresSelection) {
    return <BusinessPickerEmptyState memberships={memberships} />;
  }
  return <>{children}</>;
}

function BusinessPickerEmptyState({
  memberships,
}: {
  memberships: { id: string; name: string }[];
}) {
  const setActiveBusiness = useActiveBusinessStore((s) => s.setActiveBusiness);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '64px 24px',
        textAlign: 'center',
        minHeight: 480,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 32,
          background: palette.accentWash,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: palette.accent,
          marginBottom: 20,
        }}
      >
        <Building2 size={28} strokeWidth={1.5} />
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          color: admin.header,
          marginBottom: 8,
        }}
      >
        Pick a business to see your money
      </div>
      <div
        style={{
          fontSize: 14,
          color: admin.caption,
          maxWidth: 360,
          lineHeight: 1.5,
          marginBottom: 24,
        }}
      >
        You're a member of more than one business. Choose which one to work in.
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          width: '100%',
          maxWidth: 360,
        }}
      >
        {memberships.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => {
              void setActiveBusiness(m.id);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              borderRadius: 10,
              border: `1px solid ${palette.g150}`,
              background: palette.white,
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left',
              transition: 'all 150ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = palette.accentSoft;
              e.currentTarget.style.background = palette.accentWash;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = palette.g150;
              e.currentTarget.style.background = palette.white;
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: palette.accentWash,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: palette.accent,
                flexShrink: 0,
              }}
            >
              <Building2 size={16} strokeWidth={1.75} />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 14,
                fontWeight: 600,
                color: admin.header,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {m.name}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
